import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Mail, Lock, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Une erreur est survenue. Réessayez.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const path = mode === 'login' ? 'login' : 'register';
      const payload = mode === 'login'
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await axios.post(`${API}/auth/${path}`, payload, { withCredentials: true });
      setUser(data);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="auth-page">
      <div className="bg-white border border-neutral-200 rounded-2xl p-8 shadow-sm">
        <div className="text-center mb-6">
          <Link to="/" className="text-3xl font-black tracking-tight">
            <span className="text-[#00847A]">World</span><span className="text-[#FF4D4D]">Market</span>
          </Link>
          <h1 className="text-2xl font-black text-[#262626] mt-4">
            {mode === 'login' ? 'Connexion à votre compte' : 'Créer un compte'}
          </h1>
          <p className="text-sm text-[#595959] mt-1">
            {mode === 'login' ? 'Retrouvez vos commandes et vos favoris' : 'Rejoignez WorldMarket en quelques secondes'}
          </p>
        </div>

        <button
          onClick={handleGoogle}
          data-testid="google-login-btn"
          className="w-full flex items-center justify-center gap-3 border border-neutral-300 rounded-full h-12 font-semibold text-[#262626] hover:bg-neutral-50"
          style={{ transition: 'background-color 150ms ease' }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C40.9 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
          Continuer avec Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-neutral-200" />
          <span className="text-xs text-[#595959]">ou</span>
          <div className="flex-1 h-px bg-neutral-200" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <Label htmlFor="name">Nom complet</Label>
              <div className="relative mt-1">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input id="name" value={form.name} onChange={set('name')} data-testid="register-name" className="pl-9" placeholder="Jean Dupont" required />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input id="email" type="email" value={form.email} onChange={set('email')} data-testid="auth-email" className="pl-9" placeholder="jean@exemple.fr" required />
            </div>
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input id="password" type="password" value={form.password} onChange={set('password')} data-testid="auth-password" className="pl-9" placeholder="••••••••" minLength={6} required />
            </div>
          </div>

          {error && <div className="text-sm text-[#FF4D4D] bg-red-50 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}

          <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-12 font-semibold disabled:opacity-70">
            {loading ? <Loader2 size={18} className="animate-spin" /> : (mode === 'login' ? 'Se connecter' : 'Créer mon compte')}
          </Button>
        </form>

        <p className="text-center text-sm text-[#595959] mt-5">
          {mode === 'login' ? 'Pas encore de compte ?' : 'Déjà inscrit ?'}{' '}
          <button
            onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
            data-testid="auth-toggle"
            className="text-[#00847A] font-semibold hover:underline"
          >
            {mode === 'login' ? 'Créer un compte' : 'Se connecter'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
